import { useEffect, useState } from "react";
import api, { formatINR, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Banknote, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Pagination from "@/components/Pagination";

const PAGE_SIZE = 10;

const CATEGORIES = [
  { key: "general", label: "General" },
  { key: "teacher_salary", label: "Teacher Salary" },
  { key: "rent", label: "Rent" },
  { key: "utilities", label: "Utilities (electricity/water/net)" },
  { key: "supplies", label: "Supplies / Stationery" },
  { key: "food", label: "Food / Mess" },
  { key: "maintenance", label: "Maintenance" },
  { key: "marketing", label: "Marketing" },
  { key: "other", label: "Other" },
];

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", category: "general", notes: "", spent_on: "" });
  const [page, setPage] = useState(1);
  const { user } = useAuth();

  const load = async () => {
    const { data } = await api.get("/expenses");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const payload = { amount: parseFloat(form.amount), category: form.category, notes: form.notes };
      if (form.spent_on) payload.spent_on = new Date(form.spent_on).toISOString();
      await api.post("/expenses", payload);
      toast.success("Expense recorded");
      setForm({ amount: "", category: "general", notes: "", spent_on: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthTotal = items
    .filter((x) => new Date(x.spent_on) >= monthStart)
    .reduce((s, x) => s + (x.amount || 0), 0);
  const grandTotal = items.reduce((s, x) => s + (x.amount || 0), 0);

  return (
    <div className="p-6 md:p-10 max-w-[1200px] mx-auto" data-testid="expenses-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="label-caps mb-2 flex items-center gap-2"><Banknote size={12} /> Cash Book</div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Expenses</h1>
          <p className="text-slate-500 mt-1">Expenses reduce the "Fees Collected" figure on the dashboard.</p>
        </div>
        <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => setOpen(true)} data-testid="add-expense-button">
          <Plus size={16} className="mr-2" /> Add Expense
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card className="p-5"><div className="label-caps mb-2">This month</div><div className="text-2xl font-bold text-red-700" style={{ fontFamily: "Chivo" }}>− {formatINR(monthTotal)}</div></Card>
        <Card className="p-5"><div className="label-caps mb-2">All-time</div><div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>{formatINR(grandTotal)}</div></Card>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left">
                <th className="px-4 py-3 label-caps">Date</th>
                <th className="px-4 py-3 label-caps">Category</th>
                <th className="px-4 py-3 label-caps">Notes</th>
                <th className="px-4 py-3 label-caps">Recorded by</th>
                <th className="px-4 py-3 label-caps text-right">Amount</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((x) => (
                <tr key={x.id} data-testid={`expense-row-${x.id}`}>
                  <td className="px-4 py-3 text-xs">{formatDate(x.spent_on)}</td>
                  <td className="px-4 py-3 capitalize">{x.category}</td>
                  <td className="px-4 py-3 text-slate-600">{x.notes || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{x.recorded_by || "-"}</td>
                  <td className="px-4 py-3 text-right font-semibold text-red-700">− {formatINR(x.amount)}</td>
                  <td className="px-4 py-3 text-right">
                    {user?.role === "super_admin" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" data-testid={`delete-expense-${x.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this expense?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {formatINR(x.amount)} · {x.notes || x.category} — this will restore the amount to "Fees Collected". Cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => del(x.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">No expenses recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pageSize={PAGE_SIZE} total={items.length} onPageChange={setPage} testIdPrefix="expenses-pagination" />
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Expense</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-caps">Amount (₹) *</Label>
              <Input data-testid="exp-amount" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-2 h-11" placeholder="e.g. 2500" />
            </div>
            <div>
              <Label className="label-caps">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="exp-category" className="mt-2 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">Notes / Description</Label>
              <Textarea data-testid="exp-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-2" placeholder="e.g. Electricity bill for June" />
            </div>
            <div>
              <Label className="label-caps">Date (optional — defaults to today)</Label>
              <Input data-testid="exp-date" type="date" value={form.spent_on} onChange={(e) => setForm({ ...form, spent_on: e.target.value })} className="mt-2 h-11" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={create} disabled={!form.amount} data-testid="save-expense">
              Save Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
